import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, LoadScriptNext, Marker } from "@react-google-maps/api";
import { getFieldResponseProcessedMapPoints } from "apis/field-response-processed.api";
import env from "environments/env";
import styles from "./map.page.module.scss";

const mapPoints = [
  {
    id: "p-1",
    x: 330.6,
    y: 274.95,
    barangay: "Barangay San Roque",
    sentiment: "Negative",
    color: "#10b981",
    location: "Los Banos, Laguna",
    coordinates: "14.1697, 121.2419",
    campaignCode: "LAG003",
    dateTime: "4/9/2026, 9:30:00 AM",
    enumerator: "Carlos Domingo",
    tone: "enthusiastic",
    concerns: ["None"],
    summary:
      "Very positive about waste segregation program. Commends barangay officials for environmental initiatives and cleanliness drives.",
    demographics: {
      age: "45-54",
      gender: "Female",
      employment: "Government",
      yearsInArea: "Born Here",
      socialClass: "AB",
      education: "College Grad",
    },
    politicalProfile: {
      voterStatus: "Returning",
      voteIntent: "Definite",
      nationalGov: "Helpful",
      localGov: "Helpful",
    },
    valuesMedia: {
      coreValues: "Community",
      primaryMedia: "Facebook",
    },
    leaderPreferences: {
      desired: ["Transparent", "Accountable", "Servant"],
      rejected: ["Corrupt", "Greedy"],
    },
  },
  {
    id: "p-2",
    x: 222.8,
    y: 131.7,
    barangay: "Barangay Maligaya",
    sentiment: "Negative",
    color: "#ef4444",
    location: "Sta. Cruz, Laguna",
    coordinates: "14.2818, 121.4166",
    campaignCode: "LAG007",
    dateTime: "4/10/2026, 10:15:00 AM",
    enumerator: "Ana Mendoza",
    tone: "concerned",
    concerns: ["Flood control", "Public transport"],
    summary:
      "Respondent expressed concern over flood-prone roads and lack of consistent transport during peak hours.",
    demographics: {
      age: "35-44",
      gender: "Male",
      employment: "Private Employee",
      yearsInArea: "15 years",
      socialClass: "C1",
      education: "College Level",
    },
    politicalProfile: {
      voterStatus: "Returning",
      voteIntent: "Leaning",
      nationalGov: "Mixed",
      localGov: "Needs Improvement",
    },
    valuesMedia: {
      coreValues: "Safety",
      primaryMedia: "Facebook",
    },
    leaderPreferences: {
      desired: ["Decisive", "Approachable"],
      rejected: ["Absent", "Corrupt"],
    },
  },
  {
    id: "p-3",
    x: 173.4,
    y: 92.1,
    barangay: "Barangay Riverside",
    sentiment: "Negative",
    color: "#ef4444",
    location: "Pila, Laguna",
    coordinates: "14.2311, 121.3659",
    campaignCode: "LAG011",
    dateTime: "4/10/2026, 1:05:00 PM",
    enumerator: "Ramon Bautista",
    tone: "critical",
    concerns: ["Healthcare access"],
    summary:
      "Respondent highlighted long waiting times in public clinics and limited medicine availability.",
    demographics: {
      age: "55-64",
      gender: "Female",
      employment: "Self-employed",
      yearsInArea: "30 years",
      socialClass: "C2",
      education: "High School",
    },
    politicalProfile: {
      voterStatus: "Returning",
      voteIntent: "Undecided",
      nationalGov: "Needs Improvement",
      localGov: "Needs Improvement",
    },
    valuesMedia: {
      coreValues: "Healthcare",
      primaryMedia: "TV",
    },
    leaderPreferences: {
      desired: ["Transparent", "Compassionate"],
      rejected: ["Greedy"],
    },
  },
  {
    id: "p-4",
    x: 248.8,
    y: 181.65,
    barangay: "Barangay Poblacion",
    sentiment: "Neutral",
    color: "#3b82f6",
    location: "Nagcarlan, Laguna",
    coordinates: "14.1364, 121.4160",
    campaignCode: "LAG014",
    dateTime: "4/11/2026, 8:45:00 AM",
    enumerator: "Maria Santos",
    tone: "measured",
    concerns: ["Road maintenance"],
    summary:
      "Mixed sentiment toward local programs, with strong support for education but concerns about road upkeep.",
    demographics: {
      age: "25-34",
      gender: "Male",
      employment: "Freelancer",
      yearsInArea: "8 years",
      socialClass: "C1",
      education: "College Grad",
    },
    politicalProfile: {
      voterStatus: "New",
      voteIntent: "Leaning",
      nationalGov: "Helpful",
      localGov: "Mixed",
    },
    valuesMedia: {
      coreValues: "Education",
      primaryMedia: "YouTube",
    },
    leaderPreferences: {
      desired: ["Accountable", "Consistent"],
      rejected: ["Corrupt"],
    },
  },
  {
    id: "p-5",
    x: 483.8,
    y: 345.45,
    barangay: "Barangay Bay",
    sentiment: "Positive",
    color: "#10b981",
    location: "Calauan, Laguna",
    coordinates: "14.1498, 121.3158",
    campaignCode: "LAG021",
    dateTime: "4/11/2026, 3:20:00 PM",
    enumerator: "Joel Reyes",
    tone: "optimistic",
    concerns: ["None"],
    summary: "Strong approval of barangay-led livelihood programs and youth employment efforts.",
    demographics: {
      age: "45-54",
      gender: "Male",
      employment: "Business Owner",
      yearsInArea: "Born Here",
      socialClass: "AB",
      education: "College Grad",
    },
    politicalProfile: {
      voterStatus: "Returning",
      voteIntent: "Definite",
      nationalGov: "Helpful",
      localGov: "Helpful",
    },
    valuesMedia: {
      coreValues: "Livelihood",
      primaryMedia: "Facebook",
    },
    leaderPreferences: {
      desired: ["Transparent", "Pro-people"],
      rejected: ["Greedy"],
    },
  },
  {
    id: "p-6",
    x: 651.2,
    y: 497.55,
    barangay: "Barangay Cabuyao",
    sentiment: "Negative",
    color: "#ef4444",
    location: "Cabuyao, Laguna",
    coordinates: "14.2471, 121.1367",
    campaignCode: "LAG028",
    dateTime: "4/12/2026, 11:00:00 AM",
    enumerator: "Liza Cruz",
    tone: "frustrated",
    concerns: ["Traffic", "Water supply"],
    summary:
      "Resident cited worsening traffic and irregular water service as top local pain points.",
    demographics: {
      age: "35-44",
      gender: "Female",
      employment: "Private Employee",
      yearsInArea: "12 years",
      socialClass: "C1",
      education: "College Grad",
    },
    politicalProfile: {
      voterStatus: "Returning",
      voteIntent: "Undecided",
      nationalGov: "Mixed",
      localGov: "Needs Improvement",
    },
    valuesMedia: {
      coreValues: "Reliability",
      primaryMedia: "TikTok",
    },
    leaderPreferences: {
      desired: ["Decisive", "Responsive"],
      rejected: ["Absent", "Corrupt"],
    },
  },
  {
    id: "p-7",
    x: 328.6,
    y: 285.3,
    barangay: "Barangay Calamba",
    sentiment: "Neutral",
    color: "#f59e0b",
    location: "Calamba, Laguna",
    coordinates: "14.2110, 121.1653",
    campaignCode: "LAG031",
    dateTime: "4/12/2026, 4:10:00 PM",
    enumerator: "Mark Villanueva",
    tone: "reserved",
    concerns: ["Job opportunities"],
    summary: "Respondent sees progress in services but wants stronger local job creation programs.",
    demographics: {
      age: "25-34",
      gender: "Female",
      employment: "Unemployed",
      yearsInArea: "5 years",
      socialClass: "C2",
      education: "College Level",
    },
    politicalProfile: {
      voterStatus: "New",
      voteIntent: "Leaning",
      nationalGov: "Helpful",
      localGov: "Mixed",
    },
    valuesMedia: {
      coreValues: "Employment",
      primaryMedia: "Facebook",
    },
    leaderPreferences: {
      desired: ["Accountable", "Visible"],
      rejected: ["Corrupt"],
    },
  },
  {
    id: "p-8",
    x: 210.6,
    y: 120.3,
    barangay: "Barangay Victoria",
    sentiment: "Positive",
    color: "#10b981",
    location: "Victoria, Laguna",
    coordinates: "14.2276, 121.3271",
    campaignCode: "LAG036",
    dateTime: "4/13/2026, 9:05:00 AM",
    enumerator: "Paolo Mercado",
    tone: "hopeful",
    concerns: ["None"],
    summary:
      "Respondent appreciated local scholarship support and highlighted trust in barangay leadership.",
    demographics: {
      age: "18-24",
      gender: "Male",
      employment: "Student",
      yearsInArea: "Born Here",
      socialClass: "C1",
      education: "College",
    },
    politicalProfile: {
      voterStatus: "First-time",
      voteIntent: "Definite",
      nationalGov: "Helpful",
      localGov: "Helpful",
    },
    valuesMedia: {
      coreValues: "Education",
      primaryMedia: "YouTube",
    },
    leaderPreferences: {
      desired: ["Transparent", "Youth-focused"],
      rejected: ["Greedy"],
    },
  },
];

const DEFAULT_MAP_CENTER = { lat: 12.8797, lng: 121.774 };

const toTitleCase = (value) => {
  const normalized = String(value || "neutral").toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const getPointRows = (payload) => {
  const source = payload?.data || payload || {};
  const rows = [
    source,
    source?.data,
    source?.points,
    source?.mapPoints,
    source?.items,
    source?.result,
  ].find((candidate) => Array.isArray(candidate));

  return Array.isArray(rows) ? rows : [];
};

const getSentimentColor = (sentiment) => {
  const normalized = String(sentiment || "neutral").toLowerCase();
  if (normalized === "positive") return "#22c55e";
  if (normalized === "negative") return "#ef4444";
  return "#f59e0b";
};

const normalizeMapPoint = (item, index) => {
  const latitude = Number(item?.latitude ?? item?.lat);
  const longitude = Number(item?.longitude ?? item?.lng);
  const sentiment = toTitleCase(item?.sentiment || "neutral");
  const coordinates =
    Number.isFinite(latitude) && Number.isFinite(longitude) ? `${latitude}, ${longitude}` : "-";

  return {
    id:
      item?.fieldResponseProcessedId ||
      item?.field_response_processed_id ||
      item?.fieldResponseId ||
      item?.id ||
      `map-point-${index}`,
    latitude,
    longitude,
    barangay:
      item?.barangay ||
      item?.location ||
      item?.address ||
      `Response ${item?.fieldResponseProcessedId || index + 1}`,
    sentiment,
    color: getSentimentColor(sentiment),
    location: item?.location || item?.address || "Survey response location",
    coordinates,
    campaignCode: item?.campaignCode || localStorage.getItem("campaignCode") || "-",
    dateTime: item?.dateTime || item?.date || item?.createdAt || "-",
    enumerator: item?.enumerator || item?.name || item?.collectedBy || "-",
    tone: item?.tone || String(item?.sentiment || "neutral").toLowerCase(),
    concerns: Array.isArray(item?.concerns) && item.concerns.length ? item.concerns : ["None"],
    summary:
      item?.summary || item?.remarks || item?.notes || "No summary available for this map point.",
    demographics: {
      age: item?.age || item?.ageBand || "-",
      gender: item?.gender || item?.sex || "-",
      employment: item?.employment || item?.employmentSector || "-",
      yearsInArea: item?.yearsInArea || "-",
      socialClass: item?.socialClass || item?.category || "-",
      education: item?.education || "-",
    },
    politicalProfile: {
      voterStatus: item?.voterStatus || "-",
      voteIntent: item?.voteIntent || "-",
      nationalGov: item?.nationalGov || "-",
      localGov: item?.localGov || "-",
    },
    valuesMedia: {
      coreValues: item?.coreValues || "-",
      primaryMedia: item?.primaryMedia || "-",
    },
    leaderPreferences: {
      desired: Array.isArray(item?.desiredLeaderPreferences)
        ? item.desiredLeaderPreferences
        : ["-"],
      rejected: Array.isArray(item?.rejectedLeaderPreferences)
        ? item.rejectedLeaderPreferences
        : ["-"],
    },
  };
};

const normalizeMapPoints = (payload) =>
  getPointRows(payload)
    .map(normalizeMapPoint)
    .filter(
      (point) =>
        Number.isFinite(point.latitude) &&
        Number.isFinite(point.longitude) &&
        !(point.latitude === 0 && point.longitude === 0),
    );

const mapPinIcon = (sentiment, isSelected = false, maps) => {
  const color = getSentimentColor(sentiment);
  const markerSize = isSelected ? 32 : 28;
  const center = markerSize / 2;
  const innerRadius = isSelected ? 10 : 11;
  const outerRadius = isSelected ? 14 : 13;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${markerSize}" height="${markerSize}" viewBox="0 0 ${markerSize} ${markerSize}">
      <circle cx="${center}" cy="${center}" r="${outerRadius}" fill="${color}" fill-opacity="0.22" stroke="${isSelected ? "#ffffff" : color}" stroke-opacity="${isSelected ? 0.95 : 0.3}" stroke-width="${isSelected ? 3 : 4}" />
      <circle cx="${center}" cy="${center}" r="${innerRadius}" fill="${color}" stroke="${isSelected ? "#ffffff" : color}" stroke-width="${isSelected ? 2 : 0}" />
    </svg>`;
  const icon = {
    url: `data:image/svg+xml;base64,${btoa(svg)}`,
  };

  if (maps) {
    icon.anchor = new maps.Point(center, center);
    icon.scaledSize = new maps.Size(markerSize, markerSize);
  }

  return icon;
};

const MapPage = () => {
  const [selectedPointId, setSelectedPointId] = useState(null);
  const [mapPointPayload, setMapPointPayload] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const [mapError, setMapError] = useState("");
  const mapRef = useRef(null);
  const mapCenterRef = useRef(DEFAULT_MAP_CENTER);
  const detailPanelRef = useRef(null);

  const apiMapPoints = useMemo(() => normalizeMapPoints(mapPointPayload), [mapPointPayload]);
  const legacyMapPoints = useMemo(
    () =>
      mapPoints
        .map((point, index) => {
          const [latitude, longitude] = String(point.coordinates || "")
            .split(",")
            .map((value) => Number(value.trim()));
          return normalizeMapPoint({ ...point, latitude, longitude }, index);
        })
        .filter((point) => Number.isFinite(point.latitude) && Number.isFinite(point.longitude)),
    [],
  );
  const displayPoints = mapPointPayload ? apiMapPoints : legacyMapPoints;

  const selectedPoint = useMemo(
    () => displayPoints.find((point) => String(point.id) === String(selectedPointId)) || null,
    [displayPoints, selectedPointId],
  );

  const recenterMap = useCallback(() => {
    if (!mapRef.current || displayPoints.length === 0 || !window.google?.maps) return;

    if (displayPoints.length === 1) {
      mapRef.current.setCenter({ lat: displayPoints[0].latitude, lng: displayPoints[0].longitude });
      mapRef.current.setZoom(14);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    displayPoints.forEach((point) => {
      bounds.extend({ lat: point.latitude, lng: point.longitude });
    });
    mapRef.current.fitBounds(bounds, 48);
  }, [displayPoints]);

  useEffect(() => {
    let isMounted = true;

    setIsLoading(true);
    setMapError("");

    getFieldResponseProcessedMapPoints()
      .then((payload) => {
        if (!isMounted) return;
        setMapPointPayload(payload);
        setSelectedPointId(null);
      })
      .catch((error) => {
        if (!isMounted) return;
        setMapError(error?.data?.message || error?.message || "Failed to load map points.");
        setMapPointPayload({ data: { points: [] } });
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    recenterMap();
  }, [recenterMap]);

  const getSentimentClass = (sentiment) => {
    if (sentiment === "Positive") return styles.badgePositive;
    if (sentiment === "Neutral") return styles.badgeNeutral;
    return styles.badgeNegative;
  };

  const handleMarkerSelect = useCallback((pointId) => {
    setSelectedPointId(pointId);

    if (window.innerWidth <= 1140) {
      detailPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const googleMapsApiKey = env.GOOGLE_MAP_API_KEY || "";
  const mapCenter = displayPoints.length
    ? { lat: displayPoints[0].latitude, lng: displayPoints[0].longitude }
    : DEFAULT_MAP_CENTER;
  if (mapCenterRef.current.lat !== mapCenter.lat || mapCenterRef.current.lng !== mapCenter.lng) {
    mapCenterRef.current = mapCenter;
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>Geographic Sentiment Map</h1>
        <p>Interactive visualization of survey responses across Laguna</p>
      </div>

      <div className={styles.legendRow}>
        <div className={styles.legendItem}>
          <span className={styles.legendDotPositive} />
          <span>Positive</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.legendDotNeutral} />
          <span>Neutral</span>
        </div>
        <div className={styles.legendItem}>
          <span className={styles.legendDotNegative} />
          <span>Negative</span>
        </div>
      </div>

      <div className={styles.contentGrid}>
        <section className={styles.mapPanel}>
          <div className={styles.mapSurface}>
            {googleMapsApiKey ? (
              <LoadScriptNext googleMapsApiKey={googleMapsApiKey}>
                <GoogleMap
                  onLoad={(map) => {
                    mapRef.current = map;
                    setIsMapReady(true);
                    window.google.maps.event.trigger(map, "resize");
                    recenterMap();
                  }}
                  onUnmount={() => {
                    mapRef.current = null;
                    setIsMapReady(false);
                  }}
                  onClick={() => setSelectedPointId(null)}
                  mapContainerClassName={styles.mapGoogleCanvas}
                  mapContainerStyle={{ width: "100%", height: "500px" }}
                  center={mapCenterRef.current}
                  zoom={6}
                  options={{
                    disableDefaultUI: true,
                    draggable: true,
                    gestureHandling: "greedy",
                    zoomControl: true,
                    styles: [
                      { elementType: "geometry", stylers: [{ color: "#0f172a" }] },
                      { elementType: "labels.text.fill", stylers: [{ color: "#94a3b8" }] },
                      { elementType: "labels.text.stroke", stylers: [{ color: "#0f172a" }] },
                      {
                        featureType: "road",
                        elementType: "geometry",
                        stylers: [{ color: "#1e293b" }],
                      },
                      {
                        featureType: "road",
                        elementType: "labels",
                        stylers: [{ visibility: "off" }],
                      },
                      { featureType: "poi", stylers: [{ visibility: "off" }] },
                      {
                        featureType: "water",
                        elementType: "geometry",
                        stylers: [{ color: "#0b2443" }],
                      },
                    ],
                  }}
                >
                  {isMapReady &&
                    displayPoints.map((point) => (
                      <Marker
                        key={point.id}
                        position={{ lat: point.latitude, lng: point.longitude }}
                        title={`${point.location} (${point.sentiment})`}
                        icon={mapPinIcon(
                          point.sentiment,
                          String(selectedPointId) === String(point.id),
                          window.google.maps,
                        )}
                        onClick={() => {
                          handleMarkerSelect(point.id);
                          if (mapRef.current) {
                            mapRef.current.panTo({ lat: point.latitude, lng: point.longitude });
                            const currentZoom = Number(mapRef.current.getZoom?.() ?? 10);
                            mapRef.current.setZoom(Math.max(currentZoom, 13));
                          }
                        }}
                      />
                    ))}
                </GoogleMap>
              </LoadScriptNext>
            ) : (
              <div className={styles.mapApiNotice}>Google Maps API key is not configured.</div>
            )}

            {isLoading && (
              <div className={styles.mapLoadingOverlay} role="status" aria-live="polite">
                <span>Loading map points...</span>
              </div>
            )}

            <div className={styles.overlayBox}>
              <strong>Survey Coverage Map</strong>
              <p>{displayPoints.length} plotted locations</p>
              {mapError && <p>{mapError}</p>}
            </div>
          </div>
        </section>

        <section className={styles.detailPanel} ref={detailPanelRef}>
          {selectedPoint ? (
            <div className={styles.recordCard}>
              <div className={styles.recordCardInner}>
                <div className={styles.cardHeader}>
                  <h3>Recording Details</h3>
                  <button
                    type="button"
                    className={styles.closeButton}
                    onClick={() => setSelectedPointId(null)}
                    aria-label="Close details"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6 6 18" />
                      <path d="m6 6 12 12" />
                    </svg>
                  </button>
                </div>

                <div className={styles.cardScroll}>
                  <div className={styles.cardSection}>
                    <p className={styles.cardLabel}>Location</p>
                    <div className={styles.locationRow}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      <div>
                        <p className={styles.cardValue}>{selectedPoint.location}</p>
                        <p className={styles.cardMuted}>{selectedPoint.barangay}</p>
                        <p className={styles.cardCoordinates}>{selectedPoint.coordinates}</p>
                      </div>
                    </div>
                  </div>

                  <div className={styles.cardSection}>
                    <p className={styles.cardLabel}>Date & Campaign</p>
                    <p className={styles.cardValue}>{selectedPoint.dateTime}</p>
                    <div className={styles.codeChip}>{selectedPoint.campaignCode}</div>
                  </div>

                  <div className={styles.cardSection}>
                    <p className={styles.cardLabel}>Enumerator</p>
                    <p className={styles.cardValue}>{selectedPoint.enumerator}</p>
                  </div>

                  <div className={styles.cardSection}>
                    <p className={styles.cardLabel}>Sentiment & Tone</p>
                    <div className={styles.badgeRow}>
                      <span
                        className={`${styles.badge} ${getSentimentClass(selectedPoint.sentiment)}`}
                      >
                        {selectedPoint.sentiment.toUpperCase()}
                      </span>
                      <span className={`${styles.badge} ${styles.badgeTone}`}>
                        {selectedPoint.tone}
                      </span>
                    </div>
                  </div>

                  <div className={styles.cardSection}>
                    <p className={styles.cardLabel}>Key Concerns</p>
                    <div className={styles.concernRow}>
                      {selectedPoint.concerns.map((concern) => (
                        <span key={`${selectedPoint.id}-${concern}`} className={styles.concernTag}>
                          {concern}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className={styles.cardSection}>
                    <p className={styles.cardLabel}>Summary</p>
                    <p className={styles.cardSummary}>{selectedPoint.summary}</p>
                  </div>

                  <div className={styles.cardSection}>
                    <p className={styles.cardLabel}>Demographics</p>
                    <div className={styles.metaList}>
                      <p>
                        <span>Age:</span> {selectedPoint.demographics.age}
                      </p>
                      <p>
                        <span>Gender:</span> {selectedPoint.demographics.gender}
                      </p>
                      <p>
                        <span>Employment:</span> {selectedPoint.demographics.employment}
                      </p>
                      <p>
                        <span>Years in Area:</span> {selectedPoint.demographics.yearsInArea}
                      </p>
                      <p>
                        <span>Social Class:</span> {selectedPoint.demographics.socialClass}
                      </p>
                      <p>
                        <span>Education:</span> {selectedPoint.demographics.education}
                      </p>
                    </div>
                  </div>

                  <div className={styles.cardSection}>
                    <p className={styles.cardLabel}>Political Profile</p>
                    <div className={styles.metaList}>
                      <p>
                        <span>Voter Status:</span> {selectedPoint.politicalProfile.voterStatus}
                      </p>
                      <p>
                        <span>Vote Intent:</span> {selectedPoint.politicalProfile.voteIntent}
                      </p>
                      <p>
                        <span>National Gov:</span> {selectedPoint.politicalProfile.nationalGov}
                      </p>
                      <p>
                        <span>Local Gov (Mayor):</span> {selectedPoint.politicalProfile.localGov}
                      </p>
                    </div>
                  </div>

                  <div className={styles.cardSection}>
                    <p className={styles.cardLabel}>Values & Media</p>
                    <div className={styles.metaList}>
                      <p>
                        <span>Core Values:</span> {selectedPoint.valuesMedia.coreValues}
                      </p>
                      <p>
                        <span>Primary Media:</span> {selectedPoint.valuesMedia.primaryMedia}
                      </p>
                    </div>
                  </div>

                  <div className={`${styles.cardSection} ${styles.cardSectionLast}`}>
                    <p className={styles.cardLabel}>Leader Preferences</p>
                    <div className={styles.preferenceGroup}>
                      <p>Desired:</p>
                      <div className={styles.badgeRow}>
                        {selectedPoint.leaderPreferences.desired.map((item) => (
                          <span
                            key={`${selectedPoint.id}-desired-${item}`}
                            className={`${styles.badge} ${styles.badgeDesired}`}
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className={styles.preferenceGroup}>
                      <p>Rejected:</p>
                      <div className={styles.badgeRow}>
                        {selectedPoint.leaderPreferences.rejected.map((item) => (
                          <span
                            key={`${selectedPoint.id}-rejected-${item}`}
                            className={`${styles.badge} ${styles.badgeRejected}`}
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.detailPrompt}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <p>Click on a marker to view details</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default MapPage;
